import { TOLT_AGENT_AUTH_ENABLED } from "./branding";

let capture: ((error: unknown) => void) | undefined;
const pending: unknown[] = [];

/** Also used by React's caught/recoverable error hooks and bootstrap failures. */
export function reportToltyClientError(error: unknown) {
  if (!TOLT_AGENT_AUTH_ENABLED) return;
  if (capture) capture(error);
  else if (pending.length < 20) pending.push(error);
}

/** Runtime configuration keeps the public DSN and release aligned with the deployed server. */
export async function initToltyClientErrors() {
  if (!TOLT_AGENT_AUTH_ENABLED) return;
  window.addEventListener("error", (event) =>
    reportToltyClientError(event.error ?? new Error(event.message)),
  );
  window.addEventListener("unhandledrejection", (event) => reportToltyClientError(event.reason));
  try {
    const [Sentry, response] = await Promise.all([
      import("@sentry/browser"),
      fetch("/api/tolty/error-reporting", { cache: "no-store" }),
    ]);
    if (!response.ok)
      throw new Error(`Error reporting configuration returned HTTP ${response.status}`);
    const config: unknown = await response.json();
    if (
      !config ||
      typeof config !== "object" ||
      !("dsn" in config) ||
      typeof config.dsn !== "string"
    )
      return;
    Sentry.init({
      dsn: config.dsn,
      release:
        "release" in config && typeof config.release === "string" ? config.release : undefined,
      environment:
        "environment" in config && typeof config.environment === "string"
          ? config.environment
          : "production",
      sampleRate:
        "sampleRate" in config && typeof config.sampleRate === "number" ? config.sampleRate : 1,
      sendDefaultPii: false,
      // Global listeners above buffer failures while the SDK/configuration load.
      integrations: (defaults) => [
        ...defaults.filter((integration) => integration.name !== "GlobalHandlers"),
        Sentry.httpClientIntegration({
          failedRequestStatusCodes: [[500, 599]],
          failedRequestTargets: [window.location.origin],
        }),
      ],
      initialScope: { tags: { service: "tolty-web" } },
      beforeSend(event) {
        delete event.request;
        delete event.user;
        delete event.breadcrumbs;
        return event;
      },
    });
    capture = (error) => {
      Sentry.captureException(error);
    };
    for (const error of pending.splice(0)) capture(error);
  } catch (error) {
    // Keep the app usable, but make a broken reporting path visible locally.
    console.error("Could not initialize Tolty error reporting", error);
  }
}
